

import axios from 'axios';
import { userPool } from '../authentication/AuthContext';

const API_BASE_URL = process.env.REACT_APP_RESEARCH_API_URL;

const isAwsApi = API_BASE_URL.includes("amazonaws.com");

function parseApiResponse(response) {
  if (isAwsApi) {
    // AWS integration returns the data more directly
    return response.data.data;
  } else {
    // Local integration requires parsing JSON again
    const bodyData = JSON.parse(response.data.body);
    return bodyData.data;
  }
}

// Helper function to get the JWT token
const getAuthToken = async () => {
  const user = userPool.getCurrentUser();
  if (!user) {
    console.error('No user found in userPool');
    throw new Error('No user logged in');
  }

  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        // console.error('Session error:', err);
        reject(new Error('Authentication failed'));
      } else {
        resolve(session.getIdToken().getJwtToken());
      }
    });
  });
};

// Create an authenticated axios instance
const createAuthenticatedRequest = async (method, url, data = null) => {
  try {
    const token = await getAuthToken();
    
    const config = {
      method,
      url,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    };
    
    if (data) {
      config.data = data;
    }


    
    return axios(config);
  } catch (error) {
    // console.error('Authentication error:', error);
    throw error;
  }
};

export const api = {
  getPreviousResearches: async (userID) => {
    console.log("load previous researches, time is", new Date().toISOString());
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/research/${userID}`);
      const result = parseApiResponse(response);
      console.log("previous researches result ", result);
      return result;

    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  deleteResearch: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/remove/${userID}/${researchID}`);
      console.log('API response:', response.data);
      return response.data;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getPreviousResearch: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/research/${userID}/${researchID}`);
      const result = parseApiResponse(response);
      console.log("get previous survey result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  createSharedLink: async (userID, researchID, expiresInDays = 90) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/share`, {
        userID,
        researchID,
        expiresInDays,
      });
      const result = parseApiResponse(response);
      console.log("create link result ", result);
      return result;

    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  deleteSharedLinks: async (shareIDs) => {
    try {
      // POST to /research/share/remove with IDs in body
      // User ID is extracted from Auth Token in Lambda
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/share/remove`, {
        shareIDs
      });
      console.log('API response (delete shared):', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // MARK-THIS
  createSurvey: async (userID, surveyDescription, researchType = 'general') => {
    // TODO: remove this once we have a better way to handle known info
    const knownInfo = ["age range", "education", "employment status", "gender", "household situation", "income", "marital status"];


    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/create`, {
        userID,
        surveyDescription,
        researchType,
        knownInfo
      });
      const resultCreate = parseApiResponse(response);
      console.log("create survey ", resultCreate);
      return resultCreate;

    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  updateSurvey: async (userID, researchID, questions_list, surveyContext = '') => {
    try {
      const questions = questions_list.map(question => ({
        questionID: question.questionID,
        prompt: question.questionText,
        kind: question.questionType,
        vocab: question.responseOptions,
        max_length: question.maxLength,
        max: question.selectMax,
        min: question.selectMin,
        responsePercentages: question.responsePercentages,
        hasFixedPercentages: question.hasFixedPercentages,
      }));

      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/update_questions`, {
        userID,
        researchID,
        questions,
        surveyContext
      });
      const result = parseApiResponse(response);
      console.log("update survey result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  addContext: async (userID, researchID, surveyContext) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/update_survey_context`, {
        userID,
        researchID,
        surveyContext
      });
      const result = parseApiResponse(response);
      console.log("add context result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  updateResearch: async (userID, researchID, params = {}) => {
    try {
      const payload = {
        userID,
        researchID,
        ...params  // This can contain title and/or description
      };
      
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/update`, payload);
      const result = parseApiResponse(response);
      console.log("update research result ", result);
      return result;
    } catch (error) {
      console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  regenerateSurvey: async (userID, researchID, surveyDescription, researchType = 'general') => {
    // TODO: remove this once we have a better way to handle known info
    const knownInfo = ["age range", "education", "employment status", "gender", "household situation", "income", "marital status"];


    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/regenerate`, {
        userID,
        researchID,
        surveyDescription,
        researchType,
        knownInfo
      });
      const resultCreate = parseApiResponse(response);
      console.log("Regenerate survey ", resultCreate);
      return resultCreate;

    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  selectPersonas: async (params) => {
    if (!params.userID || !params.researchID) {
      throw new Error('User ID and Research ID are required');
    }
    try {

      const requestBody = {
        ...params.filters,
        userID: params.userID,
        researchID: params.researchID,
        numberOfPersonas: parseInt(params.filters.numberOfPersonas)
      };

      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/personas/create`, requestBody);
      const result = parseApiResponse(response);
      console.log("create personas result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  importPersonas: async (params) => {
    if (!params.userID || !params.researchID) {
      throw new Error('User ID and Research ID are required');
    }
    try {

      const requestBody = {
        userID: params.userID,
        researchID: params.researchID,
        personas: params.personas,
        fileName: params.fileName,
      };

      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/personas/import`, requestBody);
      const result = parseApiResponse(response);
      console.log("import personas result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getPreviousPersonas: async (userID, researchID) => {
    if (!userID || !researchID) {
      throw new Error('User ID and Research ID are required');
    }
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/personas/${userID}/${researchID}`);
      const result = parseApiResponse(response);
      console.log("previous personas result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getAllPreviousPersonas: async (userID) => {
    if (!userID) {
      throw new Error('User ID is required');
    }
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/personas/${userID}`);
      const result = parseApiResponse(response);
      console.log("all previous personas result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getExistingResponses: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/responses/${userID}/${researchID}`);
      const result = parseApiResponse(response);
      console.log("existing responses result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getPreviousResponseData: async (userID) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/responses/${userID}`);
      const result = parseApiResponse(response);
      console.log("all previous responses result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },
  
  generateResponses: async (userID, researchID, model="openai_default") => {
    await createAuthenticatedRequest('post', `${API_BASE_URL}/responses/create`, {
      userID,
      researchID,
      model
    });
  },

  generateQuestionResponses: async (userID, researchID, questionID) => {
    await createAuthenticatedRequest('post', `${API_BASE_URL}/responses/update_question`, {
      userID,
      researchID,
      questionID
    });
  },

  getDashboardStats: async () => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/dashboard/stats`);
      return response.data;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getDashboardData: async (dataType, filterField = null, filterValue = null) => {
    try {
      let url = `${API_BASE_URL}/dashboard/data/${dataType}`;
      if (filterField && filterValue) {
        url += `?filter_field=${filterField}&filter_value=${filterValue}`;
      }
      const response = await createAuthenticatedRequest('get', url);
      return response.data;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  deleteDashboardRecord: async (dataType, id) => {
    try {
      const response = await createAuthenticatedRequest('delete', `${API_BASE_URL}/dashboard/data/${dataType}/${id}`);
      return response.data;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  generateAnalysis: async (userID, researchID, researchQuestion) => {

    try {
      const requestBody = {
        userID,
        researchID,
        researchQuestion
      };

      const response = await createAuthenticatedRequest(
        'post',
        `${API_BASE_URL}/analysis/survey/generate`,
        requestBody
      );

      console.log('API response (generateAnalysis):', response.data);

      const result = parseApiResponse(response);
      console.log("Analysis generation result", result);
      return result;
    } catch (error) {
      // console.error('API Error (generateAnalysis):', error.response?.data || error.message);
      throw error;
    }
  },

  getSavedAnalysis: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest(
        'get',
        `${API_BASE_URL}/analysis/${userID}/${researchID}/survey`
      );
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error (getSavedAnalysis):', error.response?.data || error.message);
      throw error;
    }
  },


  generateMCResponsesAnalysis: async (
    userID,
    researchID,
    researchQuestion,
    totalCounts,
    groupedData,
    questionID
  ) => {
    try {
      const requestBody = {
        userID,
        researchID,
        researchQuestion,
        totalCounts,
        groupedData,
        questionID
      };
      const response = await createAuthenticatedRequest(
        'post',
        `${API_BASE_URL}/analysis/mc_responses/generate`,
        requestBody
      );
      const result = parseApiResponse(response);
      console.log("MC responses analysis result:", result);
      return result;
    } catch (error) {
      throw error;
    }
  },

    generateOpenEndedAnalysis: async (userID, researchID, researchQuestion, question, responses, questionID) => {
    try {
      const requestBody = {
        userID,
        researchID,
        researchQuestion,
        question,
        responses,
        questionID
      };
      const response = await createAuthenticatedRequest(
        'post',
        `${API_BASE_URL}/analysis/open_ended/generate`,
        requestBody
      );
      const result = parseApiResponse(response);
      console.log("Open-ended analysis result:", result);
      return result;
    } catch (error) {
      throw error;
    }
  },

  getSavedQuestionAnalyses: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest(
        'get',
        `${API_BASE_URL}/analysis/${userID}/${researchID}/questions`
      );
      const result = parseApiResponse(response);
      console.log("Saved question analyses:", result);
      return result;
    } catch (error) {
      // console.error('Error fetching saved question analyses:', error.response?.data || error.message);
      throw error;
    }
  },

  getRemainingAttempts: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest(
        'get',
        `${API_BASE_URL}/analysis/${userID}/${researchID}/attempts`
      );
      const result = parseApiResponse(response);
      console.log("Remaining attempts:", result);
      return result;
    } catch (error) {
      // console.error('Error fetching remaining attempts:', error.response?.data || error.message);
      throw error;
    }
  },
  
  decrementAttempt: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest(
        'post',
        `${API_BASE_URL}/analysis/${userID}/${researchID}/decrement`
      );
      const result = parseApiResponse(response);
      console.log("Attempts decremented:", result);
      return result;
    } catch (error) {
      // console.error('Error decrementing attempts:', error.response?.data || error.message);
      throw error;
    }
  },

  duplicateResearch: async (userID, researchID, title) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/duplicate`, {
        userID,
        researchID,
        title
      });
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Poll APIs
  getAllPolls: async () => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/polls`);
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getPoll: async (pollId) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/polls/${pollId}`);
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  createPoll: async (data) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/polls/create`, data);
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  deletePoll: async (pollId) => {
    try {
      const response = await createAuthenticatedRequest('delete', `${API_BASE_URL}/polls/${pollId}`);
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  generateFullAnalysis: async (userID, researchID, researchQuestion, questions) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/analysis/full/generate`, {
        userID,
        researchID,
        researchQuestion,
        questions
      });
      return parseApiResponse(response);
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getAnalysisStatus: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/analysis/${userID}/${researchID}/status`);
      return parseApiResponse(response);
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  waitForAnalysisCompletion: async (userID, researchID) => {
    const POLL_INTERVAL = 2000; // Poll every 2 seconds
    const MAX_ATTEMPTS = 30; // Maximum 1 minute of polling
    let attempts = 0;

    while (attempts < MAX_ATTEMPTS) {
      const status = await api.getAnalysisStatus(userID, researchID);
      
      switch (status.status) {
        case 'COMPLETED':
          return status;
        case 'ERROR':
          throw new Error(status.error_msg || 'Analysis failed');
        case 'PENDING':
          // Wait before polling again
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
          attempts++;
          continue;
        default:
          throw new Error(`Unknown status: ${status.status}`);
      }
    }

    throw new Error('Analysis timed out');
  },
};

